import React, { useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { ReactSurveyModel } from "survey-react";

import {
    ApiRequest,
    PersonRequest,
    ProfileData,
    WHATA_ENDPOINTS,
} from "@plinknz/tah-website-elements";

import { CustomerForm, OnFileUploadProps } from "./customer-form";
import { CustomerFormQuestionTypes, PageConfig } from "./customer-form-types";
import {
    AttachmentResponse,
    GrantPayload,
    attachmentEndpoint,
    benefitEndpoint,
    eventEndpoint,
    interactionEndpoint,
    personEndpoint,
} from "../../../config/forms";

import { Loader } from "../../loader";
import { whataRequest } from "../../../service/member/whata";
import { UserContext, UserData } from "../../../service/reducers/user";
import { fetchUser } from "../../../utility/load-member";
import { demacronise } from "../../../utility/demacronise";
import { contactApi } from "../../../service/forms/contact/api";
import { MESSAGE_ENDPOINT } from "../../../config/forms/contact";

interface BenefitFormProps {
    createSurveyConfig: (
        member: PersonRequest,
        user?: UserData
    ) => { pages: PageConfig[] };
    transformData: (
        survey: ReactSurveyModel,
        member: PersonRequest
    ) => GrantPayload;
    questionTypes: CustomerFormQuestionTypes;
    request?: ApiRequest;
    messageRequest?: ApiRequest;
    publicForm?: boolean;
}

export const BenefitForm = ({
    createSurveyConfig,
    transformData,
    questionTypes,
    request = whataRequest,
    messageRequest = contactApi,
    publicForm = false,
}: BenefitFormProps) => {
    const [loading, updateLoading] = useState<boolean>(false);
    const [apiLoading, updateApiLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>(null);
    const [recaptcha, setRecaptcha] = useState<string>(null);
    const [payload, setPayload] = useState<GrantPayload>(null);
    const {
        state: { user, member },
        dispatch,
    } = useContext(UserContext);
    const history = useHistory();
    const location = useLocation();
    const { get, post } = request;

    async function populateUser() {
        if (!loading) {
            updateLoading(true);
            await fetchUser({
                dispatch,
                history,
                id: user.id,
                location,
            });
            updateLoading(false);
        }
    }

    async function checkLoginStale() {
        // Stale token will result in a non-json (empty) response whivh will
        // cause the get function to throw an error

        try {
            await get<ProfileData>(WHATA_ENDPOINTS.me());
        } catch (_) {
            dispatch({ type: "set_member", member: null });
            populateUser();
        }
    }

    useEffect(() => {
        if (user?.id) {
            if (member) {
                checkLoginStale();
            } else {
                populateUser();
            }
        }
    }, [user?.id]);

    if (loading) {
        return (
            <div className="splash">
                <Loader size="large" />
            </div>
        );
    }

    if (!member && !publicForm) {
        return <div data-testid="no-member" />;
    }

    const surveyConfig = createSurveyConfig(member, user);

    async function uploadFile({
        file,
        targetId,
    }: {
        file: File;
        targetId: number;
    }) {
        if (!targetId) {
            throw new Error("Can not upload file without a targetId");
        }

        const safeFile = new File([file], demacronise(file.name));
        const formData = new FormData();

        formData.append("file", safeFile);
        const { data } = await post(attachmentEndpoint(), formData);
        await post(attachmentEndpoint(data as number), {
            members: [targetId],
        });
        const files = ((await get(
            personEndpoint(targetId, "attachment")
        )) as unknown) as AttachmentResponse;

        const { url } = files.data.find(
            ({ id }: { id: number }) => id === data
        );
        return { file, content: url };
    }

    const uploadFiles = async ({ options, targetId }: OnFileUploadProps) => {
        const promises: Promise<{ file: File; content: string }>[] = [];
        for (const file of options.files) {
            promises.push(
                uploadFile({ file, targetId: targetId || member.id })
            );
        }

        const uploadedFiles = await Promise.all(promises);
        options.callback("success", uploadedFiles);
    };

    const setData = async (survey: ReactSurveyModel) => {
        const payloadFromForm = transformData(survey, member);
        setPayload(payloadFromForm);
    };

    const sendDataToServer = async () => {
        updateApiLoading(true);
        const {
            targetId,
            data: { benefit, email, event, interaction },
        } = payload;

        try {
            if (benefit) {
                await post(benefitEndpoint(targetId), benefit);
            } else if (event) {
                await post(eventEndpoint(targetId), event);
            }

            if (interaction) {
                await post(interactionEndpoint(targetId), interaction);
            }

            if (email) {
                const data = {
                    data: email,
                    recaptcha,
                    tenant: process.env.TAH_TENANT,
                };

                await messageRequest.post(MESSAGE_ENDPOINT, data);
            }
        } catch (sendError) {
            console.error(sendError);

            if (sendError instanceof Error) {
                setError(sendError.message);
            }
        } finally {
            updateApiLoading(false);
        }
    };

    useEffect(() => {
        if (payload && recaptcha) {
            sendDataToServer();
        }
    }, [payload, recaptcha]);

    return (
        <CustomerForm
            onComplete={setData}
            apiRequestInProgress={apiLoading}
            apiError={error}
            surveyConfig={surveyConfig}
            questionTypes={questionTypes}
            onFileUpload={uploadFiles}
            setRecaptcha={setRecaptcha}
        />
    );
};
